import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme, pageTitleStyle, pageTitleTextStyle } from '../../theme';

const BASE = '/api';
function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { 'Content-Type': 'application/json', ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
}

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function monthShort(ym, lang) {
  if (!ym || !String(ym).includes('-')) return '—';
  const [y, m] = String(ym).split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  const s = d.toLocaleDateString(lang || 'fr', { month: 'short', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}
const fmtEur = v => (v != null && v !== '')
  ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(parseFloat(v)) + ' €'
  : '—';

export default function DeclarationsCA() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await authFetch(`${BASE}/polices/demandes-declarations`);
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Erreur'); setRows([]); return; }
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Nombre total de périodes en attente (pour le compteur d'en-tête).
  const totalPending = rows.reduce((s, r) => s + (Array.isArray(r.pending) ? r.pending.length : 0), 0);

  return (
    <div>
      <div style={{ ...pageTitleStyle, marginBottom: 16 }}>
        <span style={pageTitleTextStyle}>{t('gf_declarations.title')}</span>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: theme.textSecondary }}>
          {rows.length} {rows.length !== 1 ? t('gf_declarations.count_plural') : t('gf_declarations.count')}
        </span>
      </div>

      {error && <div style={{ color: theme.statut.decline.fg, marginBottom: 12, fontSize: 13 }}>⚠ {error}</div>}

      {loading ? (
        <div style={{ padding: 24, color: theme.textSecondary, fontSize: 14 }}>{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: theme.textTertiary, fontSize: 14,
          background: theme.surface, borderRadius: theme.radiusLg, border: `0.5px solid ${theme.border}` }}>
          {t('gf_declarations.empty')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `0.5px solid ${theme.borderStrong}`,
                color: theme.textTertiary, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                {[t('gf_declarations.col_client'), t('gf_declarations.col_broker'), t('gf_declarations.col_period'),
                  t('gf_declarations.col_declared'), t('gf_declarations.col_amount'), t('gf_declarations.col_actions')].map((h, i) => (
                  <th key={i} style={{ textAlign: i >= 4 ? 'right' : 'left', padding: '10px 12px', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const pending = Array.isArray(r.pending) ? r.pending : [];
                const first = pending[0] || {};
                const extra = pending.length - 1;
                return (
                  <tr key={r.id} style={{ borderBottom: `0.5px solid ${theme.border}` }}>
                    <td style={{ padding: '11px 12px' }}>
                      <button onClick={() => navigate(`/clients/${r.client_id}/detail`)}
                        style={{ background: 'none', border: 'none', padding: 0, color: theme.text, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                        {r.client_nom || '—'}
                      </button>
                      {r.client_pays && <div style={{ fontSize: 11, color: theme.textTertiary, marginTop: 1 }}>{r.client_pays}</div>}
                    </td>
                    <td style={{ padding: '11px 12px', color: theme.text }}>{r.courtier_nom || '—'}</td>
                    <td style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ color: theme.text }}>{monthShort(first.start_month, lang)} → {monthShort(first.end_month, lang)}</span>
                      {extra > 0 && (
                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#9A5A00', background: '#FEF3E2', border: '1px solid #FCD9A6', borderRadius: 99, padding: '1px 7px' }}>
                          +{extra}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '11px 12px', whiteSpace: 'nowrap', fontSize: 12, color: theme.textSecondary }}>{fmtDate(first.declared_at)}</td>
                    <td style={{ padding: '11px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, color: theme.text }}>{fmtEur(first.total_realise)}</div>
                      <div style={{ fontSize: 11, color: theme.textTertiary }}>{t('gf_declarations.vs_prev')} {fmtEur(first.total_previsionnel)}</div>
                      {first.over_forecast && (
                        <div style={{ marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, color: '#0F6E56', background: '#E1F5EE', borderRadius: 99, padding: '2px 8px' }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0F6E56" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6 -6l4 4l8 -8M21 7v4M21 7h-4" /></svg>
                          {t('gf_declarations.over_forecast')}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '11px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => navigate(`/gf/${r.id}`)}
                        style={{ background: theme.orange, color: '#fff', border: 'none', borderRadius: 7,
                          padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                        {t('gf_declarations.review_btn')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
